export const translations = {
  en: {
    meta: {
      title: "E-commerce Stores & B2B Platforms | Market Engine - Online Store Development",
      description: "We build B2B e-commerce stores for wholesalers and retail online stores for sellers. Multilingual stores, product catalog optimization, professional photos, descriptions and product names.",
      keywords: "e-commerce, b2b store, online store, wholesale platform, multilingual store, product catalog, allegro sellers, online shop development"
    },
    navigation: {
      services: 'Services',
      portfolio: 'Portfolio',
      advantages: 'Why Us',
      pricing: 'Pricing',
      contact: 'Contact'
    },
    hero: {
      titleLine1: 'Online Stores',
      titleLine2: 'That Sell',
      subtitle: 'We build B2B e-commerce platforms for wholesalers and retail online stores for sellers. Multilingual stores with optimized product catalogs — photos, descriptions, and names that convert.',
      exploreServices: 'Explore Services',
      startProject: 'Start Your Project'
    },
    services: {
      title: 'What We Do',
      subtitle: 'Comprehensive e-commerce solutions — from B2B wholesale platforms to retail stores for marketplace sellers.',
      webDev: 'B2B E-commerce',
      webDevDesc: 'Dedicated B2B online stores for wholesalers with customer-specific pricing, bulk ordering, and integrations with your ERP system.',
      webMobile: 'Retail Online Stores',
      webMobileDesc: 'Professional online stores for retailers and marketplace sellers — including Allegro sellers looking for their own sales channel.',
      maintenance: 'Store Maintenance',
      maintenanceDesc: 'Ongoing updates, security patches, performance optimization, and technical support for your online store.',
      ecommerce: 'Multilingual Stores',
      ecommerceDesc: 'Stores in Polish, English, and other languages. Expand your sales to international markets with localized shopping experience.',
      seo: 'Product Catalog Optimization',
      seoDesc: 'Professional product photos, compelling descriptions, and optimized product names that make your offer stand out and sell better.',
      hosting: 'Hosting & Domain',
      hostingDesc: 'We provide complete hosting and database solutions. You only pay for the domain name.',
      learnMore: 'Learn More',
      documentation: 'Documentation'
    },
    seoGuide: {
      title: 'Complete SEO Guide',
      subtitle: 'Discover the secrets of effective website positioning. Learn how to make your website visible to potential customers.',
      sections: {
        whatIsSeo: {
          title: 'What is SEO?',
          content: `SEO (Search Engine Optimization) is the process of optimizing a website for search engines. 
          It's not just about technical aspects, but primarily about creating valuable content for users. 
          Good SEO ensures your website is visible to potential customers exactly when they're looking for products or services you offer.`
        },
        whyImportant: {
          title: 'Why is SEO More Important than the Website Itself?',
          content: `Even the most beautiful website won't bring benefits if no one can find it. 
          SEO is the bridge between your website and potential customers. Effective SEO provides:
          • Constant flow of organic traffic without paying for ads
          • Building brand credibility
          • Better conversion because you reach people actively looking for your services
          • Long-term results that work for you 24/7`
        },
        basicElements: {
          title: 'Basic SEO Elements',
          content: `1. Technical SEO:
          • Page loading speed
          • Responsiveness (mobile-first)
          • Proper URL structure
          • Secure connection (HTTPS)

          2. Content:
          • Unique and valuable texts
          • Proper heading structure
          • Meta tags optimization
          • Regular updates

          3. Linking:
          • Internal link structure
          • Acquiring valuable external links
          • Fixing "dead" links`
        },
        keywords: {
          title: 'Keywords - The Foundation of SEO',
          content: `Choosing the right keywords is the foundation of SEO. Focus on:
          • Phrases that users actually search for
          • Long-tail keywords - they're less competitive
          • Search intent - whether the user is looking for information or wants to buy
          • Local phrases if you operate in a specific location`
        },
        technical: {
          title: 'Technical Aspects of SEO',
          content: `Technical optimization is key to success:
          • Page speed - users and Google value fast websites
          • Mobile-first - most traffic comes from mobile devices
          • Data structure - help Googlebot understand your site
          • XML Sitemap - help search engines index your site
          • Robots.txt - control what gets indexed`
        },
        linking: {
          title: 'Linking in SEO',
          content: `Links remain one of the most important ranking factors:
          • Internal linking - build a strong site structure
          • External linking - acquire links from valuable sources
          • Anchor text - use diverse, natural texts
          • Broken links - regularly check and fix non-working links`
        },
        monitoring: {
          title: 'Monitoring Results',
          content: `Regular checking of SEO effects allows for continuous improvements:
          • Google Search Console - track positions and clicks
          • Google Analytics - analyze traffic and user behavior
          • SEO tools - monitor positions and competition
          • Conversions - measure actual business effects`
        },
        timeline: {
          title: 'How Long Does SEO Take?',
          content: `SEO is a marathon, not a sprint:
          • 3-6 months - first noticeable effects
          • 6-12 months - stable positions for main phrases
          • 12+ months - dominance in search results
          
          Remember: SEO requires patience and consistency, but the effects are long-lasting.`
        },
        social: {
          title: 'Social Media and SEO',
          content: `Social media supports SEO through:
          • Increasing reach and brand recognition
          • Generating website traffic
          • Building natural links
          • Increasing user engagement`
        },
        bestPractices: {
          title: 'SEO Best Practices',
          content: `1. Create content for users, not robots
          2. Regularly update content
          3. Take care of technical aspects
          4. Build natural link profiles
          5. Monitor and optimize results
          6. Stay up to date with SEO trends
          7. Remember about local SEO
          8. Use structured data`
        }
      },
      cta: 'Want to learn more about SEO for your business?',
      contact: 'Contact us'
    },
    portfolio: {
      title: 'Our Stores',
      visitSite: 'Visit Store',
      getSimilar: 'Order a Similar Store',
      kraft: {
        title: 'Kraft & Tools',
        description: 'B2B e-commerce platform for a tool wholesaler with an extensive product catalog and customer-specific pricing.',
        features: ['B2B Pricing', 'Product Catalog', 'Wholesale Orders', 'Fast Search']
      },
      iglak: {
        title: 'Iglak Mierzejewski',
        description: 'Online store for a garden center with optimized product presentation and multilingual support.',
        features: ['Product Optimization', 'Multilingual', 'Inventory Sync', 'Analytics']
      }
    },
    comparison: {
      title: 'Why Choose Our E-commerce',
      myApproach: 'Our Stores',
      competition: 'Template Stores',
      advantages: {
        1: 'Dedicated B2B and retail stores built to your needs',
        2: 'Multilingual stores — sell in Polish, English, and more',
        3: 'Optimized product catalogs — photos, descriptions, names',
        4: 'Individual approach and payment after completion',
        5: 'Integration with ERP, Allegro, and payment systems'
      },
      disadvantages: {
        1: 'Generic templates not suited for wholesalers',
        2: 'Single-language only — no international sales',
        3: 'Poor product presentation with default descriptions',
        4: 'Upfront payment with no guarantee of satisfaction',
        5: 'No integrations with external systems'
      }
    },
    advantages: {
      title: 'The Market Engine Advantage',
      modern: {
        title: 'Specialized E-commerce',
        desc: 'B2B stores for wholesalers and retail stores for marketplace sellers'
      },
      professional: {
        title: 'Multilingual Stores',
        desc: 'Sell in Polish, English, and other languages from one platform'
      },
      fast: {
        title: 'Catalog Optimization',
        desc: 'Professional photos, descriptions, and product names that sell'
      }
    },
    pricing: {
      title: 'Individual Pricing',
      subtitle: 'Every store is different — contact us for a tailored quote.',
      b2b: {
        title: 'B2B Store',
        desc: 'Dedicated wholesale platform with customer-specific pricing, bulk ordering, and ERP integration.'
      },
      retail: {
        title: 'Retail Store',
        desc: 'Online store for retailers and marketplace sellers, including Allegro sellers looking for their own sales channel.'
      },
      combo: {
        title: 'B2B + Retail 2-in-1',
        desc: 'One platform combining wholesale B2B and retail store. Sell to businesses and individual customers from a single store.'
      },
      contactUs: 'Get a Quote'
    },
    contact: {
      title: 'Start Your Project',
      subtitle: 'Get in touch for a free consultation',
      email: 'Email',
      phone: 'Phone',
      address: 'Location',
      form: {
        name: 'Your Name',
        email: 'Your Email',
        message: 'Project Details',
        submit: 'Send Message'
      },
      calendly: {
        title: 'Schedule Meeting',
        subtitle: 'Book a free consultation'
      },
      success: 'Message sent successfully!',
      error: 'Failed to send message. Please try again.'
    },
    footer: {
      accountNumber: 'Account Number',
      rights: '© 2025 Market Engine. All rights reserved.',
      usefulResources: 'Useful Resources',
      connectWithUs: 'Contact Us'
    }
  },
  pl: {
    meta: {
      title: "Sklepy Internetowe & Platformy B2B | Market Engine - Tworzenie Sklepów Online",
      description: "Budujemy sklepy B2B dla hurtowni oraz sklepy detaliczne dla sprzedawców. Sklepy wielojęzyczne, optymalizacja katalogu produktów, profesjonalne zdjęcia, opisy i nazwy produktów.",
      keywords: "sklep internetowy, b2b, hurtownia, sklep online, platforma b2b, sklep wielojęzyczny, katalog produktów, sprzedawcy allegro, tworzenie sklepów"
    },
    navigation: {
      services: 'Usługi',
      portfolio: 'Portfolio',
      advantages: 'Dlaczego My',
      pricing: 'Wycena',
      contact: 'Kontakt'
    },
    hero: {
      titleLine1: 'Sklepy Internetowe',
      titleLine2: 'Które Sprzedają',
      subtitle: 'Budujemy platformy B2B dla hurtowni oraz sklepy detaliczne dla sprzedawców. Sklepy wielojęzyczne z zoptymalizowanym katalogiem produktów — zdjęcia, opisy i nazwy które konwertują.',
      exploreServices: 'Zobacz Usługi',
      startProject: 'Rozpocznij Projekt'
    },
    services: {
      title: 'Co Robimy',
      subtitle: 'Kompleksowe rozwiązania e-commerce — od platform B2B dla hurtowni po sklepy detaliczne dla sprzedawców.',
      webDev: 'Sklepy B2B',
      webDevDesc: 'Dedykowane sklepy B2B dla hurtowni z cenami indywidualnymi dla klientów, zamówieniami hurtowymi i integracją z systemem ERP.',
      webMobile: 'Sklepy Detaliczne',
      webMobileDesc: 'Profesjonalne sklepy internetowe dla sprzedawców detalicznych — w tym dla sprzedawców z Allegro szukających własnego kanału sprzedaży.',
      maintenance: 'Obsługa Sklepu',
      maintenanceDesc: 'Regularne aktualizacje, bezpieczeństwo, optymalizacja wydajności i wsparcie techniczne dla Twojego sklepu.',
      ecommerce: 'Sklepy Wielojęzyczne',
      ecommerceDesc: 'Sklepy w języku polskim, angielskim i innych. Rozszerz sprzedaż na rynki międzynarodowe z lokalizacją w lokalnym języku.',
      seo: 'Optymalizacja Katalogu',
      seoDesc: 'Profesjonalne zdjęcia produktów, atrakcyjne opisy i zoptymalizowane nazwy produktów które sprawiają, że oferta lepiej się prezentuje i sprzedaje.',
      hosting: 'Hosting i Domena',
      hostingDesc: 'Zapewniamy kompletne rozwiązania hostingowe i bazodanowe. Płacisz tylko za domenę.',
      learnMore: 'Dowiedz się więcej',
      documentation: 'Dokumentacja'
    },
    seoGuide: {
      title: 'Kompletny Poradnik SEO',
      subtitle: 'Poznaj tajniki skutecznego pozycjonowania stron internetowych. Dowiedz się, jak sprawić, by Twoja strona była widoczna dla potencjalnych klientów.',
      sections: {
        whatIsSeo: {
          title: 'Co to jest SEO?',
          content: `SEO (Search Engine Optimization) to proces optymalizacji strony internetowej pod kątem wyszukiwarek. 
          To nie tylko techniczne aspekty, ale przede wszystkim tworzenie wartościowych treści dla użytkowników. 
          Dobre SEO sprawia, że Twoja strona jest widoczna dla potencjalnych klientów dokładnie wtedy, gdy szukają produktów lub usług, które oferujesz.`
        },
        whyImportant: {
          title: 'Dlaczego SEO jest ważniejsze niż sama strona?',
          content: `Nawet najpiękniejsza strona internetowa nie przyniesie korzyści, jeśli nikt jej nie znajdzie. 
          SEO to most między Twoją stroną a potencjalnymi klientami. Skuteczne SEO zapewnia:
          • Stały dopływ organicznego ruchu bez płacenia za reklam
          • Budowanie wiarygodności marki
          • Lepszą konwersję, bo trafiasz do osób aktywnie szukających Twoich usług
          • Długoterminowe rezultaty, które pracują na Ciebie 24/7`
        },
        basicElements: {
          title: 'Podstawowe elementy SEO',
          content: `1. Techniczne SEO:
          • Szybkość ładowania strony
          • Responsywność (mobile-first)
          • Prawidłowa struktura URL
          • Bezpieczne połączenie (HTTPS)

          2. Treść:
          • Unikalne i wartościowe teksty
          • Odpowiednia struktura nagłówków
          • Optymalizacja meta tagów
          • Regularne aktualizacje

          3. Linkowanie:
          • Wewnętrzna struktura linków
          • Zdobywanie wartościowych linków zewnętrznych
          • Naprawa "martwych" linków`
        },
        keywords: {
          title: 'Słowa kluczowe - podstawa SEO',
          content: `Wybór odpowiednich słów kluczowych to fundament SEO. Należy skupić się na:
          • Frazach, których rzeczywiście szukają użytkownicy
          • Słowach z długiego ogona (long tail) - są mniej konkurencyjne
          • Intencji wyszukiwania - czy użytkownik szuka informacji, czy chce kupić
          • Lokalnych frazach, jeśli działasz w konkretnej lokalizacji`
        },
        technical: {
          title: 'Techniczne aspekty SEO',
          content: `Optymalizacja techniczna to klucz do sukcesu:
          • Szybkość strony - użytkownicy i Google cenią szybkie witryny
          • Mobile-first - większość ruchu pochodzi z urządzeń mobilnych
          • Struktura danych - ułatw Googlebot zrozumienie Twojej strony
          • XML Sitemap - pomóż wyszukiwarkom indeksować Twoją stronę
          • Robots.txt - kontroluj, co jest indeksowane`
        },
        linking: {
          title: 'Linkowanie w SEO',
          content: `Linki to nadal jeden z najważniejszych czynników rankingowych:
          • Linkowanie wewnętrzne - buduj silną strukturę strony
          • Linkowanie zewnętrzne - zdobywaj linki z wartościowych źródeł
          • Anchor text - używaj różnorodnych, naturalnych tekstów
          • Broken links - regularnie sprawdzaj i naprawiaj niedziałające linki`
        },
        monitoring: {
          title: 'Monitorowanie wyników',
          content: `Regularne sprawdzanie efektów SEO pozwala na ciągłe usprawnienia:
          • Google Search Console - śledź pozycje i kliki
          • Google Analytics - analizuj ruch i zachowania użytkowników
          • Narzędzia SEO - monitoruj pozycje i konkurencję
          • Konwersje - mierz rzeczywiste efekty biznesowe`
        },
        timeline: {
          title: 'Jak długo trwa efekt SEO?',
          content: `SEO to maraton, nie sprint:
          • 3-6 miesięcy - pierwsze zauważalne efekty
          • 6-12 miesięcy - stabilne pozycje dla głównych fraz
          • 12+ miesięcy - dominacja w wynikach wyszukiwania
          
          Pamiętaj: SEO wymaga cierpliwości i konsekwencji, ale efekty są długotrwałe.`
        },
        social: {
          title: 'Social Media a SEO',
          content: `Media społecznościowe wspierają SEO poprzez:
          • Zwiększanie zasięgu i rozpoznawalności marki
          • Generowanie ruchu na stronie
          • Budowanie naturalnych linków
          • Zwiększanie zaangażowania użytkowników`
        },
        bestPractices: {
          title: 'Najlepsze praktyki SEO',
          content: `1. Twórz content dla użytkowników, nie dla robotów
          2. Regularnie aktualizuj treści
          3. Dbaj o techniczne aspekty strony
          4. Buduj naturalne profile linków
          5. Monitoruj i optymalizuj wyniki
          6. Bądź na bieżąco z trendami SEO
          7. Pamiętaj o local SEO
          8. Wykorzystuj dane strukturalne`
        }
      },
      cta: 'Chcesz dowiedzieć się więcej o SEO dla Twojego biznesu?',
      contact: 'Skontaktuj się z nami'
    },
    portfolio: {
      title: 'Nasze Sklepy',
      visitSite: 'Zobacz Sklep',
      getSimilar: 'Zamów podobny sklep',
      kraft: {
        title: 'Kraft & Tools',
        description: 'Platforma e-commerce B2B dla hurtowni narzędzi z rozbudowanym katalogiem produktów i indywidualnym cennikiem dla klientów.',
        features: ['Ceny B2B', 'Katalog Produktów', 'Zamówienia Hurtowe', 'Szybkie Wyszukiwanie']
      },
      iglak: {
        title: 'Iglak Mierzejewski',
        description: 'Sklep internetowy dla centrum ogrodniczego z zoptymalizowaną prezentacją produktów i obsługą wielojęzyczną.',
        features: ['Optymalizacja Produktów', 'Wielojęzyczność', 'Synchronizacja Stanów', 'Analityka']
      }
    },
    comparison: {
      title: 'Dlaczego Warto Wybrać Nasze Sklepy',
      myApproach: 'Nasze Sklepy',
      competition: 'Sklepy Szablonowe',
      advantages: {
        1: 'Dedykowane sklepy B2B i detaliczne budowane pod Twoje potrzeby',
        2: 'Sklepy wielojęzyczne — sprzedawaj po polsku, angielsku i więcej',
        3: 'Zoptymalizowany katalog produktów — zdjęcia, opisy, nazwy',
        4: 'Indywidualne podejście i płatność po wykonaniu',
        5: 'Integracja z ERP, Allegro i systemami płatności'
      },
      disadvantages: {
        1: 'Ogólne szablony nieprzystosowane do hurtowni',
        2: 'Tylko jeden język — brak sprzedaży międzynarodowej',
        3: 'Słaba prezentacja produktów z domyślnymi opisami',
        4: 'Płatność z góry bez gwarancji satysfakcji',
        5: 'Brak integracji z systemami zewnętrznymi'
      }
    },
    advantages: {
      title: 'Przewaga Market Engine',
      modern: {
        title: 'Specjalistyczne E-commerce',
        desc: 'Sklepy B2B dla hurtowni i sklepy detaliczne dla sprzedawców'
      },
      professional: {
        title: 'Sklepy Wielojęzyczne',
        desc: 'Sprzedawaj po polsku, angielsku i innych językach z jednej platformy'
      },
      fast: {
        title: 'Optymalizacja Katalogu',
        desc: 'Profesjonalne zdjęcia, opisy i nazwy produktów które sprzedają'
      }
    },
    pricing: {
      title: 'Wycena Indywidualna',
      subtitle: 'Każdy sklep jest inny — skontaktuj się z nami po indywidualną wycenę.',
      b2b: {
        title: 'Sklep B2B',
        desc: 'Dedykowana platforma hurtowa z indywidualnym cennikiem dla klientów, zamówieniami hurtowymi i integracją z ERP.'
      },
      retail: {
        title: 'Sklep Detaliczny',
        desc: 'Sklep internetowy dla sprzedawców detalicznych i sprzedawców z Allegro szukających własnego kanału sprzedaży.'
      },
      combo: {
        title: 'B2B + Detal 2 w 1',
        desc: 'Jedna platforma łącząca sklep hurtowy B2B i detaliczny. Sprzedawaj firmom i klientom indywidualnym z jednego sklepu.'
      },
      contactUs: 'Zamów Wycenę'
    },
    contact: {
      title: 'Rozpocznij Projekt',
      subtitle: 'Skontaktuj się z nami po bezpłatną konsultację',
      email: 'Email',
      phone: 'Telefon',
      address: 'Lokalizacja',
      form: {
        name: 'Twoje Imię',
        email: 'Twój Email',
        message: 'Szczegóły Projektu',
        submit: 'Wyślij wiadomość'
      },
      calendly: {
        title: 'Umów spotkanie',
        subtitle: 'Zarezerwuj bezpłatną konsultację'
      },
      success: 'Wiadomość została wysłana!',
      error: 'Nie udało się wysłać wiadomości. Spróbuj ponownie.'
    },
    footer: {
      accountNumber: 'Numer konta',
      rights: '© 2025 Market Engine. Wszelkie prawa zastrzeżone.',
      usefulResources: 'Przydatne Zasoby',
      connectWithUs: 'Kontakt'
    }
  }
};
